import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, TextField, Button, IconButton, Avatar, Container, Divider, Alert, CircularProgress } from '@mui/material';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BlockIcon from '@mui/icons-material/Block';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { keyframes } from '@mui/system';
import apiClient from '../services/apiClient';

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const NOTES_STORAGE_KEY = 'alpha01_personal_notes';

const Notes = () => {
  const [searchParams] = useSearchParams();
  const [userType, setUserType] = useState('patient');
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [pilotMode, setPilotMode] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const type = searchParams.get('userType') || 'patient';
    setUserType(type);

    const init = async () => {
      // Check pilot mode status
      try {
        const response = await apiClient.get('/health/pilot-mode');
        if (response.data) {
          setPilotMode(response.data.pilotMode || false);
        }
      } catch (err) {
        console.warn('Failed to check pilot mode:', err);
      }

      // Load notes
      if (type === 'physician') {
        // For physicians, fetch clinical notes from API if patient context exists
        const patientId = searchParams.get('patientId');
        if (patientId) {
          try {
            const result = await apiClient.getPatientNotes(patientId);
            const items = result?.data || result || [];
            setNotes(Array.isArray(items) ? items.map(n => ({
              id: n.id,
              text: n.content || n.note || n.text || '',
              created_at: n.created_at,
              patient_id: n.patient_id
            })) : []);
          } catch (err) {
            console.error('Failed to load clinical notes:', err);
            setNotes([]);
          }
        } else {
          setNotes([]);
        }
      } else {
        // For patients, use localStorage for personal notes
        try {
          const stored = localStorage.getItem(NOTES_STORAGE_KEY);
          if (stored) {
            setNotes(JSON.parse(stored));
          }
        } catch {
          // Ignore parse errors
        }
      }
      setLoading(false);
    };

    init();
  }, [searchParams]);

  const pageTitle = userType === 'physician' ? 'Clinical Notes' : 'My Notes';
  const placeholderText = userType === 'physician' ? 'Add a clinical note about a patient...' : 'Add a personal note...';
  const emptyMessage = userType === 'physician'
    ? (searchParams.get('patientId') ? 'No clinical notes for this patient yet.' : 'Select a patient to view their clinical notes.')
    : 'No personal notes yet. Add your first note above.';

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      setError('Note cannot be empty.');
      return;
    }

    const noteEntry = { id: Date.now(), text: newNote.trim(), created_at: new Date().toISOString() };

    if (userType === 'physician') {
      const patientId = searchParams.get('patientId');
      if (patientId && !pilotMode) {
        try {
          await apiClient.addPatientNote(patientId, { note: newNote.trim() });
        } catch (err) {
          console.error('Failed to save clinical note:', err);
          setError('Failed to save note to server. Please try again.');
          return;
        }
      }
    } else {
      // Persist patient notes to localStorage
      const updated = [noteEntry, ...notes];
      try {
        localStorage.setItem(NOTES_STORAGE_KEY, JSON.stringify(updated));
      } catch {
        // localStorage full or unavailable
      }
    }

    setNotes([noteEntry, ...notes]);
    setNewNote('');
    setError('');
  };

  const handleDeleteNote = (noteId) => {
    const updated = notes.filter(n => n.id !== noteId);
    setNotes(updated);
    if (userType !== 'physician') {
      try {
        localStorage.setItem(NOTES_STORAGE_KEY, JSON.stringify(updated));
      } catch {
        // Ignore
      }
    }
  };

  const handleClose = () => {
    if (userType === 'physician') {
      navigate('/clinic-dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 4,
          maxWidth: 800,
          width: '100%',
          mx: 'auto',
          position: 'relative',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
          animation: `${fadeIn} 0.5s ease-out`,
          overflow: 'hidden',
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
          flexDirection: 'column'
        }}>
          <Avatar
            sx={{
              bgcolor: 'warning.main',
              width: 56,
              height: 56,
              mb: 2,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            }}
          >
            <NoteAltIcon fontSize="large" />
          </Avatar>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              textAlign: 'center'
            }}
          >
            {pageTitle}
          </Typography>
        </Box>

        {/* Pilot Mode Blocked Message */}
        {pilotMode && (
          <Alert
            severity="error"
            icon={<BlockIcon />}
            sx={{ mb: 3 }}
          >
            <strong>Feature Disabled in Pilot Mode</strong>
            <Box component="div" sx={{ mt: 1 }}>
              Clinical notes are disabled during the pilot phase to prevent PHI storage.
              This feature will be available after the pilot phase is complete.
            </Box>
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <TextField
            label={placeholderText}
            value={newNote}
            onChange={e => setNewNote(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            disabled={pilotMode}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.1)' },
                '&:hover fieldset': { borderColor: 'primary.main' },
              }
            }}
            error={!!error}
            helperText={error || (pilotMode ? 'Disabled in pilot mode' : '')}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<NoteAltIcon />}
            onClick={handleAddNote}
            disabled={pilotMode}
            sx={{
              py: 1.5, px: 3, borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)' }
            }}
          >
            Add Note
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />
        <Typography
          variant="h6"
          sx={{ mb: 2, fontWeight: 600, textAlign: 'left', color: 'text.primary' }}
        >
          Your Notes
        </Typography>

        <Box sx={{ mb: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : notes.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 3, borderRadius: 2, bgcolor: 'background.paper',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                textAlign: 'center', color: 'text.secondary'
              }}
            >
              <Typography>{emptyMessage}</Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {notes.map((note, index) => (
                <Box
                  key={note.id}
                  sx={{
                    p: 2, borderRadius: 2, bgcolor: 'background.paper',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.3s ease',
                    animation: `${fadeIn} 0.5s ease-out ${index * 0.1}s both`,
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{ textAlign: 'left', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      >
                        {note.text}
                      </Typography>
                      {note.created_at && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {new Date(note.created_at).toLocaleDateString()} {new Date(note.created_at).toLocaleTimeString()}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', ml: 2 }}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteNote(note.id)}
                        sx={{ '&:hover': { backgroundColor: 'error.light' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={handleClose}
            sx={{
              py: 1.5, px: 3, borderRadius: 2, borderWidth: 2,
              transition: 'all 0.3s ease',
              '&:hover': { borderWidth: 2, backgroundColor: 'rgba(0, 0, 0, 0.04)' }
            }}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default Notes;
